import { getActivityThresholdsValues } from 'utils';
import useUser from 'utils/useUser';
import classNames from 'classnames';
import { ReadingType } from 'infrastructure/enums';
import { type FC } from 'react';
import {
  getReadingInfo,
  getWeightRiskContent,
  hiddenThresholds,
} from 'utils/get-reading-info';

import s from './styles.module.scss';

import type {
  StatusKeys,
  TStatuses,
  TThresholdName,
  Value,
} from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { ReadingStatus } from 'infrastructure/enums/reading-statuses';
import type { IPatientThresholds } from 'infrastructure/interfaces';
import type { Nullable } from 'infrastructure/types';

interface ReadingItemProps {
  globalStatus: ReadingStatus;
  values: Value;
  statuses: TStatuses;
  thresholds: IPatientThresholds;
  readingType: ReadingType;
  thresholdName?: Nullable<TThresholdName>;
}

const ReadingItem: FC<ReadingItemProps> = ({
  globalStatus,
  statuses,
  thresholds,
  values,
  readingType,
  thresholdName,
}) => {
  const riskItems = getActivityThresholdsValues(
    globalStatus,
    values,
    statuses,
    thresholds,
    readingType,
  );
  const { isPatient, isMobileDevice } = useUser();

  const weightRiskContent = () => {
    const text = getWeightRiskContent(thresholds, thresholdName);
    if (!text) return null;
    const [risk, value] = text.split('≥ ');

    return (
      <p>
        <span>{risk}</span>{' '}
        <span>
          <b>≥ {value}</b>
        </span>
      </p>
    );
  };

  return (
    <div className={s.reading}>
      <div
        data-cy="reading-values"
        className={classNames(s['reading-items'], {
          [s['is-patient']]: isPatient && !isMobileDevice,
        })}
      >
        {Object.keys(values).map((threshold, index) => {
          if (hiddenThresholds.includes(threshold)) {
            return null;
          }
          const { className, key, value, unit } = getReadingInfo(
            statuses,
            values,
            threshold as StatusKeys,
            readingType,
          );

          return (
            <p className={s[className]} key={readingType + index}>
              <span>{key === 'spo2' ? 'SpO2' : key} </span>
              <span>{`${value} ${unit}`}</span>
            </p>
          );
        })}
      </div>
      <div data-cy="reading-thresholds" className={s['risk-items']}>
        {readingType === ReadingType.Weight
          ? weightRiskContent()
          : riskItems.length > 0 &&
            riskItems.map((riskItem: string) => {
              const [risk, value] = riskItem?.split(':');

              return (
                <p key={riskItem}>
                  <span>{risk}:</span>
                  <span>{value}</span>
                </p>
              );
            })}
      </div>
    </div>
  );
};
export default ReadingItem;
